// User-side card: lists the Microsoft 365 mailboxes the org admin has
// provisioned for the current user. Users can Enable or Disable sync per
// mailbox; they cannot add their own. Free-form email input was removed
// when we switched to admin-provisioned model.
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import apiClient from '@/api';

interface ProvisionedMailbox {
  assignmentId: string;
  email: string;
  displayName?: string;
  provider: string;
  enabled: boolean;
}

interface EnterpriseAvailability {
  mailboxes: ProvisionedMailbox[];
  providers: {
    microsoft365: {
      enabled: boolean;
      tenantId?: string | null;
      allowedDomains?: string[];
      hasProvisioned?: boolean;
      validationStatus?: string;
    };
    googleWorkspace: { enabled: boolean };
  };
}

interface Props {
  onAdded?: () => void; // refetch parent list when sync is enabled/disabled
}

export const EnterpriseMailboxCard: React.FC<Props> = ({ onAdded }) => {
  const { addNotification } = useAppStore();
  const { user } = useAuthStore();
  const [availability, setAvailability] = useState<EnterpriseAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAssignmentId, setBusyAssignmentId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.auth.getEnterpriseAvailable();
      const inner: EnterpriseAvailability = (res?.providers ? res : res?.data) || null;
      setAvailability(inner);
    } catch (err: any) {
      console.warn('Could not load provisioned mailboxes:', err?.message);
      setAvailability(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const m365 = availability?.providers?.microsoft365;
  const tenantReady = !!(m365?.enabled && m365?.validationStatus === 'valid');
  const mailboxes = availability?.mailboxes || [];

  const handleEnable = async (mb: ProvisionedMailbox) => {
    setBusyAssignmentId(mb.assignmentId);
    try {
      // Backend now requires the email to be a pre-provisioned assignment for
      // this user. We post the email; server validates and probes Graph.
      await apiClient.auth.addEnterpriseAccount({
        email: mb.email,
        providerType: 'microsoft365'
      });
      addNotification?.({
        type: 'success',
        title: 'Sync enabled',
        message: `${mb.email} is now syncing. First fetch will run shortly.`
      });
      onAdded?.();
      await load();
    } catch (err: any) {
      const raw = err?.response?.data?.error ?? err?.message ?? 'Could not enable sync';
      const msg = typeof raw === 'string' ? raw : (raw?.message || JSON.stringify(raw));
      addNotification?.({ type: 'error', title: 'Enable failed', message: msg });
    } finally {
      setBusyAssignmentId(null);
    }
  };

  const handleDisable = async (mb: ProvisionedMailbox) => {
    if (!confirm(`Stop syncing ${mb.email}? The admin assignment stays — you can re-enable later.`)) return;
    if (!user?._id) return;
    setBusyAssignmentId(mb.assignmentId);
    try {
      await apiClient.auth.removeEmailAccount(user._id, mb.email);
      addNotification?.({ type: 'success', title: 'Sync stopped', message: `${mb.email} is no longer syncing.` });
      onAdded?.();
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Disable failed', message: err?.response?.data?.error || err.message });
    } finally {
      setBusyAssignmentId(null);
    }
  };

  if (loading) return null;
  if (!m365?.enabled) return null; // Org isn't running Application Permissions — don't surface card.

  const showEmpty = mailboxes.length === 0;

  return (
    <div className="mb-6 p-5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Work Email Accounts (Organization-managed)
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Your administrator has provisioned the following Microsoft 365 mailboxes for you. Click <strong>Enable sync</strong>
            to start fetching mail through your organization's Azure app — no Microsoft sign-in required.
          </p>
        </div>
        <span
          className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            tenantReady
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
          }`}
        >
          {tenantReady ? 'Tenant ready' : `Tenant ${m365?.validationStatus || 'not configured'}`}
        </span>
      </div>

      {!tenantReady && (
        <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
          Your organization's Microsoft 365 configuration isn't validated yet. Ask your admin to confirm it in
          Organization Settings → Microsoft 365 before enabling sync.
        </p>
      )}

      {showEmpty ? (
        <div className="rounded-md bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400 italic">
          No work mailboxes have been assigned to you yet. Contact your organization administrator to provision one.
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {mailboxes.map(mb => (
            <li key={mb.assignmentId} className="flex items-center justify-between px-4 py-3 gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {mb.displayName ? `${mb.displayName} <${mb.email}>` : mb.email}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {mb.enabled ? 'Sync is on — emails fetch on schedule.' : 'Not yet enabled.'}
                </div>
              </div>
              {mb.enabled ? (
                <Button
                  variant="outline"
                  disabled={busyAssignmentId === mb.assignmentId}
                  onClick={() => handleDisable(mb)}
                >
                  {busyAssignmentId === mb.assignmentId ? <LoadingSpinner size="sm" /> : 'Disable sync'}
                </Button>
              ) : (
                <Button
                  disabled={!tenantReady || busyAssignmentId === mb.assignmentId}
                  onClick={() => handleEnable(mb)}
                >
                  {busyAssignmentId === mb.assignmentId ? <><LoadingSpinner size="sm" /> Verifying…</> : 'Enable Work Email Account'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
